/**
 * ConversationStatusBadge Component - Web Dashboard
 * Visual badge showing conversation status
 */


import { CheckCircle2, Clock, XCircle, MessageCircle } from 'lucide-react';

export type ConversationStatus = 'active' | 'pending' | 'resolved' | 'closed';

interface ConversationStatusBadgeProps {
  status: ConversationStatus;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

const STATUS_CONFIG: Record<
  ConversationStatus,
  {
    label: string;
    icon: React.ReactNode;
    bgColor: string;
    textColor: string;
    dotColor: string;
  }
> = {
  active: {
    label: 'กำลังดำเนินการ',
    icon: <MessageCircle className="w-3 h-3" />,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-700 dark:text-green-400',
    dotColor: 'bg-green-500',
  },
  pending: {
    label: 'รอตอบกลับ',
    icon: <Clock className="w-3 h-3" />,
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-700 dark:text-amber-400',
    dotColor: 'bg-amber-500',
  },
  resolved: {
    label: 'แก้ไขแล้ว',
    icon: <CheckCircle2 className="w-3 h-3" />,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-700 dark:text-blue-400',
    dotColor: 'bg-blue-500',
  },
  closed: {
    label: 'ปิดแล้ว',
    icon: <XCircle className="w-3 h-3" />,
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    textColor: 'text-gray-600 dark:text-gray-400',
    dotColor: 'bg-gray-400',
  },
};

export function ConversationStatusBadge({
  status,
  showIcon = true,
  showLabel = true,
  size = 'sm',
}: ConversationStatusBadgeProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${config.bgColor} ${config.textColor} ${sizeClasses}
      `}
    >
      {showIcon && (
        <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor} animate-pulse`} />
      )}
      {showLabel && config.label}
    </span>
  );
}

export default ConversationStatusBadge;
